import { useState } from 'react';
import type { Key } from 'react';
import { Card, Transfer, Typography, message, Spin } from 'antd';
import type { TransferDirection } from 'antd/es/transfer';
import { useList } from '@refinedev/core';
import { space } from '@viq-admin/ui';
import type { UserProfile } from '../../api/types';
import { assignRep, unassignRep } from '../../api/discovery';

interface TransferItem {
  key: string;
  title: string;
}

/**
 * Portal conference **Assignments** tab — the single-tenant counterpart to the
 * console's rep Transfer (`apps/console/src/views/ConferenceDetail/Assignments`).
 * A `company_admin` assigns/unassigns their OWN company's users to this
 * conference; assigned reps see it in the mobile app.
 *
 * The portal is single-tenant, so there is no console-style "grant company
 * access" step — the roster is simply GET /v1/admin/users (token-scoped to the
 * caller's company). Current assignment state is derived from each user's
 * `conferenceIds` (returned on the list per `UserProfile`); moving a rep calls
 * the per-rep `/v1/admin/conferences/:id/assignments/:oid` endpoints the portal
 * already uses for the user-detail Remove. Writes use `allSettled` + a refetch
 * so the two sides always reflect the true post-write state even if one call
 * fails.
 *
 * ON-DEVICE CHECK: this relies on the users list populating `conferenceIds`. If
 * the left/right split looks wrong on load (everyone shown as unassigned),
 * that contract isn't being honoured on the list endpoint and the "current"
 * state needs a dedicated GET-assignments source — the writes still work.
 */
export function ConferenceAssignments({ conferenceId }: { conferenceId: string }) {
  const [busy, setBusy] = useState(false);

  const { result, query } = useList<UserProfile>({
    resource: 'users',
    pagination: { mode: 'off' },
  });
  // The users list returns deactivated users too (the Users page hides them
  // with its default Status filter). Drop them from both sides: they can't
  // sign in, the backend refuses to assign them, and an old assignment row
  // left over from before deactivation stays inert until a reactivation.
  const users = (result?.data ?? []).filter((u) => !u.deactivatedAt);

  const dataSource: TransferItem[] = users.map((u) => ({
    key: u.oid,
    title: u.name ? `${u.name} (${u.email})` : u.email,
  }));
  const targetKeys = users
    .filter((u) => (u.conferenceIds ?? []).includes(conferenceId))
    .map((u) => u.oid);

  async function onTransfer(direction: TransferDirection, moveKeys: Key[]) {
    const oids = moveKeys.map(String);
    if (oids.length === 0) return;
    setBusy(true);
    try {
      const results = await Promise.allSettled(
        oids.map((oid) =>
          direction === 'right'
            ? assignRep(conferenceId, oid)
            : unassignRep(conferenceId, oid),
        ),
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      const ok = oids.length - failed;
      const verb = direction === 'right' ? 'Assigned' : 'Unassigned';
      if (ok > 0) message.success(`${verb} ${ok}`);
      if (failed > 0) message.error(`${failed} could not be updated`);
      // Refetch regardless: the Transfer's two sides are derived from the
      // refetched roster, so they land on the true post-write state.
      await query.refetch();
    } finally {
      setBusy(false);
    }
  }

  if (query?.isLoading) return <Spin />;

  return (
    <Card size="small" loading={busy}>
      <Typography.Text type="secondary">
        Assign your company&rsquo;s users to this conference. Assigned reps see
        it in the mobile app and can capture leads for it.
      </Typography.Text>
      <div style={{ marginTop: space.md }}>
        <Transfer<TransferItem>
          dataSource={dataSource}
          targetKeys={targetKeys}
          onChange={(_next, direction, moveKeys) =>
            void onTransfer(direction, moveKeys)
          }
          showSearch
          render={(item) => item.title}
          titles={['Unassigned', 'Assigned']}
          disabled={busy}
          style={{ width: '100%' }}
          // `minWidth: 0`: antd's own list minimum puts two lists past a
          // phone's width.
          listStyle={{ flex: 1, height: 320, minWidth: 0 }}
        />
      </div>
    </Card>
  );
}
